import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, collection, getDocs, query, where, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import Modal from 'react-modal';
import Swal from 'sweetalert2'; // Import SweetAlert2
import styles from './AdminHomepageContent.module.css';

// ---- helpers for naming and deleting ----
const slugify = (s) =>
  s.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const safeName = (file) => {
  const ext = file.name.split('.').pop().toLowerCase();
  const base = file.name.replace(/\.[^.]+$/, '');
  return `${slugify(base)}-${Date.now()}.${ext}`;
};

// Extract the Storage path from a Firebase download URL
const pathFromDownloadUrl = (url) => {
  try {
    // https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<ENCODED_PATH>?alt=media&token=...
    const afterO = url.split('/o/')[1];
    if (!afterO) return '';
    const encodedPath = afterO.split('?')[0];
    return decodeURIComponent(encodedPath);
  } catch {
    return '';
  }
};



const AdminHomepageContent = () => {
  const [contentList, setContentList] = useState([]);
  const [activeContent, setActiveContent] = useState('');
  const [newContent, setNewContent] = useState({ id: '', title: '', description: '', heroImage: '', heroText: '', sections: [], nombreHomepage: '' });
  const [categories, setCategories] = useState([]);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(false); // General loading state
  const [imageLoading, setImageLoading] = useState(false); // Image-specific loading state
  const firestore = getFirestore();
  const storage = getStorage();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchContentList = async () => {
      const querySnapshot = await getDocs(collection(firestore, 'homepageContent'));
      const contents = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filteredContents = contents.filter(content => content.id !== 'current');
      setContentList(filteredContents);

      const currentContentDoc = await getDoc(doc(firestore, 'homepageContent', 'current'));
      if (currentContentDoc.exists()) {
        const activeContentId = currentContentDoc.data().activeContent;
        if (activeContentId) {
          const activeContentDoc = await getDoc(doc(firestore, 'homepageContent', activeContentId));
          if (activeContentDoc.exists()) {
            setActiveContent(activeContentId);
          } else {
            console.warn(`Active content document with ID ${activeContentId} does not exist.`);
          }
        }
      }
      const categorySnapshot = await getDocs(collection(firestore, 'categories'));
      const categoriesList = categorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(categoriesList);
    };
    fetchContentList();
  }, [firestore]);

  const handleSetActiveContent = async (id) => {
    setLoading(true); // Show loading overlay
    try {
      const activeContentDoc = await getDoc(doc(firestore, 'homepageContent', id));
      if (activeContentDoc.exists()) {
        await setDoc(doc(firestore, 'homepageContent', 'current'), { activeContent: id });
        setActiveContent(id);
        Swal.fire('Éxito', '¡Establecido como activa exitosamente!', 'success');
      } else {
        console.error(`Document with ID ${id} does not exist.`);
      }
    } catch (error) {
      Swal.fire('Error', 'Error al establecer la vista como activa.', 'error');
    } finally {
      setLoading(false); // Hide loading overlay
    }
  };

  const handleDisableActiveContent = async () => {
    setLoading(true); // Show loading overlay
    try {
      await setDoc(doc(firestore, 'homepageContent', 'current'), { activeContent: '' });
      setActiveContent(''); // Clear active content in the UI
      Swal.fire('Éxito', '¡Contenido de la página de inicio desactivado exitosamente!', 'success');

    } catch (error) {
      Swal.fire('Error', 'Error al desactivar el contenido de la página de inicio.', 'error');

    } finally {
      setLoading(false); // Hide loading overlay
    }
  };

    const handleAddOrUpdateContent = async (e) => {
      e.preventDefault();

      if (!newContent.title || !newContent.description || !newContent.nombreHomepage) {
        Swal.fire('Error', 'Título, Descripción y Nombre de la Página de Inicio son obligatorios.', 'error');
        return;
      }

      setLoading(true);
      try {
        // stable id for this homepage doc
        const contentId = newContent.id || new Date().toISOString();

        // Upload hero image (if provided) into homepage/<contentId>/...
        let heroImageUrl = newContent.heroImage || '';
        if (heroImageFile) {
          const storageRef = ref(storage, `homepage/${contentId}/${safeName(heroImageFile)}`);
          setImageLoading(true);
          await uploadBytes(storageRef, heroImageFile);
          heroImageUrl = await getDownloadURL(storageRef);
          setImageLoading(false);
        }

        const toSave = { ...newContent, id: contentId, heroImage: heroImageUrl };
        const docRef = doc(firestore, 'homepageContent', contentId);
        await setDoc(docRef, toSave);

        // reset form
        setNewContent({ id: '', title: '', description: '', heroImage: '', heroText: '', sections: [], nombreHomepage: '' });
        setHeroImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = null;

        const querySnapshot = await getDocs(collection(firestore, 'homepageContent'));
        const contents = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setContentList(contents.filter(c => c.id !== 'current'));

        setModalIsOpen(false);
        Swal.fire('Éxito', newContent.id ? '¡Contenido actualizado exitosamente!' : '¡Contenido agregado exitosamente!', 'success');
      } catch (error) {
        console.error(error);
        Swal.fire('Error', 'Error al guardar el contenido.', 'error');
      } finally {
        setLoading(false);
      }
    };


  const handleEditContent = (content) => {
    setNewContent(content);
    setModalIsOpen(true);
  };

  const handleDeleteContent = (id) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás deshacer esta acción!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, ¡elimínalo!',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteContent(id); // Call the deletion logic if confirmed
      }
    });
  };

const deleteContent = async (id) => {
  setLoading(true);
  try {
    // find full content to know all images
    const docSnap = await getDoc(doc(firestore, 'homepageContent', id));
    const content = docSnap.exists() ? docSnap.data() : contentList.find(c => c.id === id);

    // delete hero original and webp
    if (content?.heroImage) {
      const p = pathFromDownloadUrl(content.heroImage);
      if (p) {
        await deleteObject(ref(storage, p)).catch(() => {});
        await deleteObject(ref(storage, p.replace(/(\.[^.]+)$/, '-w1200.webp'))).catch(() => {});
      }
    }

    // delete section originals + webp
    for (const section of content?.sections || []) {
      // single image sections
      if (section?.content?.image) {
        const p = pathFromDownloadUrl(section.content.image);
        if (p) {
          await deleteObject(ref(storage, p)).catch(() => {});
          await deleteObject(ref(storage, p.replace(/(\.[^.]+)$/, '-w1200.webp'))).catch(() => {});
        }
      }
      // carousel images
      for (const u of section?.content?.images || []) {
        const p = pathFromDownloadUrl(u);
        if (p) {
          await deleteObject(ref(storage, p)).catch(() => {});
          await deleteObject(ref(storage, p.replace(/(\.[^.]+)$/, '-w1200.webp'))).catch(() => {});
        }
      }
    }

    await deleteDoc(doc(firestore, 'homepageContent', id));

    const qs = await getDocs(collection(firestore, 'homepageContent'));
    const contents = qs.docs.map(d => ({ id: d.id, ...d.data() }));
    setContentList(contents.filter(c => c.id !== 'current'));

    Swal.fire('Eliminado', 'Tu contenido ha sido eliminado.', 'success');
  } catch (error) {
    console.error(error);
    Swal.fire('Error', 'Hubo un error al eliminar el contenido.', 'error');
  } finally {
    setLoading(false);
  }
};


  const closeModal = () => {
    setModalIsOpen(false);
    setNewContent({ id: '', title: '', description: '', heroImage: '', heroText: '', sections: [], nombreHomepage: '' });
    setHeroImageFile(null);
    fileInputRef.current.value = null;
  };

  const addSection = (e) => {
    e.preventDefault();
    setNewContent((prevContent) => ({
      ...prevContent,
      sections: [...prevContent.sections, { title: '', type: 'normal', content: {} }]
    }));
  };

  const updateSection = (index, updatedSection) => {
    const updatedSections = newContent.sections.map((section, idx) => (idx === index ? updatedSection : section));
    setNewContent((prevContent) => ({
      ...prevContent,
      sections: updatedSections
    }));
  };

  const removeSection = (index) => {
    setNewContent(prevContent => {
      const updatedSections = prevContent.sections.filter((_, idx) => idx !== index);
      return {
        ...prevContent,
        sections: updatedSections
      };
    });
  };

    const handleSectionImageUpload = (index, e) => {
      const file = e.target.files[0];
      if (!file) return;

      // ensure stable id for foldering
      const contentId = newContent.id || (() => {
        const id = new Date().toISOString();
        setNewContent(prev => ({ ...prev, id }));
        return id;
      })();

      const storageRef = ref(storage, `homepageSections/${contentId}/${safeName(file)}`);
      setImageLoading(true);
      uploadBytes(storageRef, file).then(async (snapshot) => {
        const downloadURL = await getDownloadURL(snapshot.ref);
        const updatedSection = {
          ...newContent.sections[index],
          content: { ...newContent.sections[index].content, image: downloadURL }
        };
        updateSection(index, updatedSection);
        setImageLoading(false);
      });
    };

  const [newLinkInput, setNewLinkInput] = useState({ platform: 'instagram', link: '' });


    const handleSectionCarouselImagesUpload = (index, e) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      const contentId = newContent.id || (() => {
        const id = new Date().toISOString();
        setNewContent(prev => ({ ...prev, id }));
        return id;
      })();

      const uploadPromises = [];
      const imageUrls = newContent.sections[index].content.images || [];
      setImageLoading(true);

      for (const file of files) {
        const storageRef = ref(storage, `homepageSections/${contentId}/${safeName(file)}`);
        const p = uploadBytes(storageRef, file)
          .then(snap => getDownloadURL(snap.ref))
          .then(url => imageUrls.push(url));
        uploadPromises.push(p);
      }

      Promise.all(uploadPromises).then(() => {
        const updatedSection = {
          ...newContent.sections[index],
          content: { ...newContent.sections[index].content, images: imageUrls }
        };
        updateSection(index, updatedSection);
        setImageLoading(false);
      });
    };


const handleDeleteCarouselImage = async (sectionIndex, imageIndex) => {
  const url = newContent.sections[sectionIndex]?.content?.images?.[imageIndex];
  const updatedImages = [...(newContent.sections[sectionIndex].content.images || [])];
  updatedImages.splice(imageIndex, 1);

  // update state immediately
  const updatedSection = {
    ...newContent.sections[sectionIndex],
    content: { ...newContent.sections[sectionIndex].content, images: updatedImages }
  };
  updateSection(sectionIndex, updatedSection);

  // try to delete original and its -w1200.webp variant in Storage
  try {
    const originalPath = pathFromDownloadUrl(url);
    if (originalPath) {
      await deleteObject(ref(storage, originalPath)).catch(() => {});
      const webpPath = originalPath.replace(/(\.[^.]+)$/, '-w1200.webp');
      await deleteObject(ref(storage, webpPath)).catch(() => {});
    }
  } catch { /* ignore */ }
};


  const formatTikTokLink = (link) => {
    const trimmedLink = link.trim();
    if (!trimmedLink.includes('/embed')) {
      const videoId = trimmedLink.split('/').pop();
      return `https://www.tiktok.com/embed/${videoId}`;
    }
    return trimmedLink;
  };
  

  const addPublicationLink = async (index) => {
    const formattedLink = formatTikTokLink(newLinkInput.link); // Format TikTok link
  
    try {
      // Fetch the current homepage content from Firestore
      const homepageDoc = await getDoc(doc(firestore, 'homepageContent', newContent.id));
      const existingSections = homepageDoc.exists() ? homepageDoc.data().sections || [] : [];
      const existingLinks =
        existingSections.find((section) => section.type === 'publications')?.content?.links || [];
  
      // Check if the link already exists in Firestore
      const isDuplicateInDatabase = existingLinks.some((link) => link.link === formattedLink);
  
      // Check if the link already exists in the local state
      const currentSectionLinks = newContent.sections[index]?.content?.links || [];
      const isDuplicateInState = currentSectionLinks.some((link) => link.link === formattedLink);
  
      if (isDuplicateInDatabase || isDuplicateInState) {
        Swal.fire('Error', 'This TikTok link already exists.', 'error');
        return;
      }
  
      // Add the new link to the local state
      const updatedSections = [...newContent.sections];
      updatedSections[index].content.links = updatedSections[index].content.links || [];
      updatedSections[index].content.links.push({ platform: 'tiktok', link: formattedLink });
  
      // Update Firestore with the updated sections array
      await setDoc(doc(firestore, 'homepageContent', newContent.id), {
        ...newContent,
        sections: updatedSections,
      });
  
      // Update the local state with the updated sections
      setNewContent((prevContent) => ({
        ...prevContent,
        sections: updatedSections,
      }));
  
      // Reset the input field
      setNewLinkInput({ platform: 'tiktok', link: '' });
  
      Swal.fire('Success', 'TikTok link added successfully!', 'success');
    } catch (error) {
      console.error('Error adding TikTok link:', error);
      Swal.fire('Error', 'Failed to add TikTok link.', 'error');
    }
  };
  
  
  
  

  const formatLink = (platform, link) => {
    let formattedLink = link.trim();
  
    if (platform === 'instagram' && !formattedLink.endsWith('/embed')) {
      formattedLink = `${formattedLink.replace(/\/$/, '')}/embed`;
    } else if (platform === 'tiktok' && !formattedLink.includes('/embed')) {
      const videoId = formattedLink.split('/').pop();
      formattedLink = `https://www.tiktok.com/embed/${videoId}`;
    }
  
    return formattedLink;
  };
  

  const fetchProductsForCategory = async (selectedCategory) => {
    try {
      const productsCollection = collection(firestore, 'products');
  
      // Query for products where 'category' matches or 'categories' contains the selectedCategory
      const productsQuery = query(
        productsCollection,
        where('categories', 'array-contains', selectedCategory)
      );
  
      const querySnapshot = await getDocs(productsQuery);
  
      // Combine results to handle both main category and subcategories
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
  
      console.log(`Fetched products for category ${selectedCategory}:`, products);
  
      // You can store or display the fetched products as needed
      return products; // Return the products array if you need it
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return [];
    }
  };
  
  


  const removePublicationLink = (sectionIndex, linkIndex) => {
    const updatedLinks = [...newContent.sections[sectionIndex].content.links];
    updatedLinks.splice(linkIndex, 1);
    updateSection(sectionIndex, { ...newContent.sections[sectionIndex], content: { links: updatedLinks } });
  };

  return (
    <div className={styles.adminHomepageContent}>
      {loading && <div className={styles.loadingOverlay}></div>} {/* Loading Overlay */}
      {imageLoading && <div className={styles.imageLoadingOverlay}></div>} {/* Image Upload Loading Overlay */}
      <h2>Administración Homepage</h2>
  
      {/* Button to add new content */}
      <div className={styles.buttonRow}>
      <button
        onClick={() => {
          // ensure a stable id for uploads before saving
          if (!newContent.id) {
            const id = new Date().toISOString();
            setNewContent((prev) => ({ ...prev, id }));
          }
          setModalIsOpen(true);
        }}
      >
        Agregar Nuevo Homepage
      </button>
      </div>
  
      {/* Display current active content */}
      <h3>Vista Actual Activa</h3>
      {contentList.filter(content => content.id === activeContent && content.nombreHomepage).map(content => (
        <div key={content.id}>
          <h4>{content.nombreHomepage}</h4> {/* Updated to display 'nombreHomepage' instead of title */}
          <button className={styles.disableButton} onClick={handleDisableActiveContent}>Deshabilitar</button>
          <button className={styles.editButton} onClick={() => handleEditContent(content)}>Editar</button>
          <button className={styles.deleteButton} onClick={() => handleDeleteContent(content.id)}>Eliminar</button>
        </div>
      ))}
  
      {/* Table for all content entries */}
      <h3>Todas las Vistas</h3>
      <table className={styles.contentTable}>
        <thead>
          <tr>
            <th>Nombre de la Vista</th> {/* Replaced Title with Nombre Homepage */}
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {contentList.map((content) => (
            <tr key={content.id}>
              <td>{content.nombreHomepage}</td> {/* Show Nombre Homepage instead of title */}
              <td className="actions">
                <button
                  className={content.id === activeContent ? styles.activeButton : styles.setActiveButton}
                  onClick={() => handleSetActiveContent(content.id)}
                >
                  {content.id === activeContent ? 'Activa' : 'Activar'}
                </button>
                <button className={styles.editButton} onClick={() => handleEditContent(content)}>Editar</button>
                <button className={styles.deleteButton} onClick={() => handleDeleteContent(content.id)}>Eliminar</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
  
      {/* Modal for adding/updating content */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Edit Homepage Content"
        className={styles.ReactModal__Content}
        overlayClassName={styles.ReactModal__Overlay}
      >
        <h2 className={styles.modalHeader}>{newContent.id ? 'Editar Vista' : 'Agregar Nueva Vista'}</h2>
        <div className={styles.modalBody}>
          <form onSubmit={handleAddOrUpdateContent}>
            <input
              type="text"
              value={newContent.nombreHomepage}
              onChange={(e) => setNewContent({ ...newContent, nombreHomepage: e.target.value })}
              placeholder="Nombre de la Vista (Solo Admin)"
              className={styles.inputField}
              required
            />
  
            <input
              type="text"
              value={newContent.title}
              onChange={(e) => setNewContent({ ...newContent, title: e.target.value })}
              placeholder="Título"
              className={styles.inputField}
              required
            />
  
            <textarea
              value={newContent.description}
              onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
              placeholder="Descripción"
              className={styles.textAreaField}
              required
            />
  
            {/* Hero Image Upload */}
            <label>Imágen Hero Portada</label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => setHeroImageFile(e.target.files[0])}
              className={styles.inputFile}
            />
            {newContent.heroImage && (
              <img
                src={newContent.heroImage}
                alt="Current Hero Image"
                className={styles.imagePreview}
              />
            )}
  
            <input
              type="text"
              value={newContent.heroText}
              onChange={(e) => setNewContent({ ...newContent, heroText: e.target.value })}
              placeholder="Texto Hero"
              className={styles.inputField}
            />
  
            <h3>Secciones</h3>
            {newContent.sections.map((section, index) => (
              <div key={index} className={styles.section}>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateSection(index, { ...section, title: e.target.value })}
                  placeholder="Título de Sección"
                />
                <select
                  value={section.type}
                  onChange={(e) => updateSection(index, { ...section, type: e.target.value, content: {} })}
                >
                  <option value="normal">Normal</option>
                  <option value="onImage">En Image</option>
                  <option value="carousel">Carrusel</option>
                  <option value="productList">Lista Productos</option>
                  <option value="reviews">Reviews</option>
                  <option value="text">Texto</option>
                  <option value="button">Botón</option>
                  <option value="socialMediaProfilesEmbed">Social Media Profiles Embed</option>
                  <option value="publications">Publications</option>
                </select>
  
                {/* Section Type: Social Media Profiles Embed */}
                {section.type === 'socialMediaProfilesEmbed' && (
                  <div>
                    <p>This section will display Instagram and Facebook profiles side by side.</p>
                  </div>
                )}
  
                {/* Section Type: Publications */}


                {section.type === 'publications' && (
  <div>
    <h4>Publication Links</h4>
    {section.content.links && section.content.links.map((link, linkIndex) => (
      <div key={linkIndex}>
        <p>Link: {link.link}</p>
        <button type="button" onClick={() => removePublicationLink(index, linkIndex)}>Remove Link</button>
      </div>
    ))}

    <input
      type="text"
      placeholder="Enter TikTok link"
      value={newLinkInput.link}
      onChange={(e) => setNewLinkInput((prev) => ({ ...prev, link: e.target.value }))}
    />
    <button
      type="button"
      onClick={() => {
        if (newLinkInput.link) {
          addPublicationLink(index);
        }
      }}
    >
      Add Link
    </button>
  </div>
)}

  
                {/* Other Section Types */}
                {/* Section Type: Normal */}
                {section.type === 'normal' && (
                  <div>
                    <input
                      type="file"
                      onChange={(e) => handleSectionImageUpload(index, e)}
                    />
                    {section.content.image && (
                      <img
                        src={section.content.image}
                        alt="Current Section Image"
                        className={styles.imagePreview}
                      />
                    )}
                    <textarea
                      value={section.content.text || ''}
                      onChange={(e) => updateSection(index, { ...section, content: { ...section.content, text: e.target.value } })}
                      placeholder="Texto"
                    />
                  </div>
                )}
  
                {/* Section Type: On Image */}
                {section.type === 'onImage' && (
                  <div>
                    <input
                      type="file"
                      onChange={(e) => handleSectionImageUpload(index, e)}
                    />
                    {section.content.image && (
                      <img
                        src={section.content.image}
                        alt="Current Section Image"
                        className={styles.imagePreview}
                      />
                    )}
                    <textarea
                      value={section.content.text || ''}
                      onChange={(e) => updateSection(index, { ...section, content: { ...section.content, text: e.target.value } })}
                      placeholder="Text"
                    />
                    <input
                      type="color"
                      value={section.content.textColor || '#000000'}
                      onChange={(e) => updateSection(index, { ...section, content: { ...section.content, textColor: e.target.value } })}
                    />
                  </div>
                )}
  
                {/* Section Type: Carousel */}
                {section.type === 'carousel' && (
                  <div>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleSectionCarouselImagesUpload(index, e)}
                    />
                    <div className={styles.carouselImageGrid}>
                      {section.content.images &&
                        section.content.images.map((imageUrl, idx) => (
                          <div key={idx} className={styles.carouselImageContainer}>
                            <img
                              src={imageUrl}
                              alt={`Carousel Image ${idx + 1}`}
                              className={styles.imagePreview}
                            />
                            <button
                              type="button"
                              className={styles.deleteCarouselButton}
                              onClick={() => handleDeleteCarouselImage(index, idx)}
                            >
                              X
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
  
{/* Section Type: Product List */}
{section.type === 'productList' && (
  <div>
    <select
      value={section.content.option || ''}
      onChange={(e) => {
        const selectedOption = e.target.value;

        // Update the section with the selected category option
        updateSection(index, { 
          ...section, 
          content: { ...section.content, option: selectedOption } 
        });

        if (selectedOption) {
          fetchProductsForCategory(selectedOption);
        }
      }}
    >
      <option value="" disabled>Seleccionar Opción</option>
      <option value="bestSelling">Más Vendidos</option>
      <option value="recentlyAdded">Agregados Recientes</option>
      {categories.map(category => (
        <option key={category.id} value={category.name}>{category.name}</option>
      ))}
    </select>
  </div>
)}

  
                {/* Section Type: Reviews */}
                {section.type === 'reviews' && (
                  <div>
                    <p>Esta sección mostrará todas las reseñas de productos como tarjetas en un formato de carrusel.</p>
                  </div>
                )}
  
                {/* Section Type: Text */}
                {section.type === 'text' && (
                  <textarea
                    value={section.content.text || ''}
                    onChange={(e) => updateSection(index, { ...section, content: { ...section.content, text: e.target.value } })}
                    placeholder="Enter your text here"
                    rows={6} // Allow for multiple lines
                  />
                )}
  
                {/* Section Type: Button */}
                {section.type === 'button' && (
                  <div>
                    <p>Esta sección añadirá un botón a la página de inicio.</p>
                  </div>
                )}
  
                <button onClick={(e) => { e.preventDefault(); removeSection(index); }}>Remover Sección</button>
              </div>
            ))}
  
            <div className={styles.modalButtonRow}>
              <button type="button" className={styles.modalAddSectionButton} onClick={addSection}>Nueva Sección</button>
              <button
                type="submit"
                className={styles.modalSubmitButton}
                disabled={imageLoading} // Disable the button while image is uploading
              >
                {newContent.id ? 'Actualizar Vista' : 'Guardar Vista'}
              </button>
              <button type="button" className={styles.modalCloseButton} onClick={closeModal}>
                Cerrar
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
  

};

export default AdminHomepageContent;
